import { useState } from "react";
import { BedDouble, HeartPulse, ClipboardList, HandHeart, ShieldCheck, Clock, Car, Users, Home, BadgeCheck, Phone, MessageCircle } from "lucide-react";
import { SITE, WA_MESSAGES, waLink, buildCaregiverMessage } from "@/config/site";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import Reveal from "@/components/Reveal";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import FaqAccordion from "@/components/FaqAccordion";

const PILLARS = [
  { icon: BedDouble, label: "Residential Care" },
  { icon: Users, label: "Professional Caregivers" },
  { icon: HeartPulse, label: "Health Monitoring" },
  { icon: HandHeart, label: "Personalised Care" },
  { icon: ShieldCheck, label: "Safe & Comfortable Environment" },
];

const HOME_CARE = [
  { id: "home-4h", hours: "4 Hours", name: "Stay Out Package", caregiver: "Local Malaysian Caregiver", days: "20 Days Per Month", price: "RM3,000", per: "/Month", color: "#075B59" },
  { id: "home-7h", hours: "7 Hours", name: "Stay Out Package", caregiver: "Local Malaysian Caregiver", days: "20 Days Per Month", price: "RM3,500", per: "/Month", color: "#E96D85" },
  { id: "home-8h", hours: "8 Hours", name: "Stay Out Package", caregiver: "Local Malaysian Caregiver", days: "20 Days Per Month", price: "RM3,800", per: "/Month", color: "#C99A52" },
  { id: "home-10h", hours: "10 Hours", name: "Stay Out Package", caregiver: "Local Malaysian Caregiver", days: "20 Days Per Month", price: "RM4,500", per: "/Month", color: "#83A96A" },
  { id: "home-12h", hours: "12 Hours", name: "Stay Out Package", caregiver: "Local Malaysian Caregiver", days: "20 Days Per Month", price: "RM5,500", per: "/Month", color: "#075B59" },
  { id: "home-10h-cert", hours: "10 Hours", name: "Stay Out Package", caregiver: "Certified Malaysian / Filipino Caregiver", days: "28 Days Per Month", price: "RM5,500", per: "/Month", color: "#E96D85" },
  { id: "home-flexi-12h", hours: "Flexi 12 Hours", name: "Flexi Package", caregiver: "Local Certified Caregiver", days: "26 Days Per Month", price: "RM6,000", per: "/Month", color: "#C99A52" },
];

const STAY_IN = [
  { id: "stay-female", name: "Local Caregiver (Malaysian Female)", caregiver: "Stay-In Full-Time Care", days: "26 Days Per Month", price: "RM6,000", per: "/Month", color: "#E96D85" },
  { id: "stay-male", name: "Local Caregiver (Malaysian Male)", caregiver: "Stay-In Full-Time Care", days: "26 Days Per Month", price: "RM6,500", per: "/Month", color: "#075B59" },
  { id: "stay-20d", name: "Local Caregiver", caregiver: "Stay-In Full-Time Care", days: "20 Days Per Month", price: "RM5,000", per: "/Month", color: "#83A96A" },
  { id: "stay-nurse", name: "Certified Nurse (Malaysian, Degree / Diploma)", caregiver: "Stay-In Full-Time Nursing Care", days: "26 Days Per Month", price: "RM8,500", per: "/Month", color: "#C99A52" },
];

const HOSPITAL_CARE = [
  { id: "hosp-sitter", name: "Hospital Sitter / Stay In Duty", note: "Cover All Hospitals", caregiver: "Hospital Care Service", days: "Per Duty", price: "RM300", per: "/24 Hours", color: "#E96D85" },
  { id: "hosp-driver-company", name: "Hospital / Clinic Appointment Driver", note: "Company Car", caregiver: "Appointment Driver Service", days: "Per Appointment", price: "RM350", per: "/Appointment", color: "#075B59" },
  { id: "hosp-driver-client", name: "Appointment Driver", note: "Using Client's Car", caregiver: "Appointment Driver Service", days: "Per Appointment", price: "RM200", per: "/Appointment", color: "#83A96A" },
];

const PACKAGE_GROUPS = [
  { label: "Home Care (Stay Out & Flexi)", items: HOME_CARE },
  { label: "Stay-In Care", items: STAY_IN },
  { label: "Hospital & Driver Services", items: HOSPITAL_CARE },
];

const ALL_PACKAGES = PACKAGE_GROUPS.flatMap((g) => g.items);

const DRIVER_POINTS = [
  "Pick up from home to hospital / clinic and return",
  "Wait for client until appointment finished",
  "Toll and fuel arrangements",
  "Use company car or client's car (RM200)",
  "Arrange monthly appointment schedule",
  "Licensed and friendly caregiver-driver",
];

const SERVICES_INCLUDED = [
  "Daily bathing, grooming & personal hygiene",
  "Assistance with daily living activities",
  "Feeding support (Oral, NG Tube, PG Tube)",
  "Physical activity and mobility support",
  "Fall prevention and safe environment",
  "Repositioning in bed for comfort and safety",
  "Basic physiotherapy & massage therapy",
  "Wound dressing and bedsore care",
  "Medication assistance / dispensing",
  "Continuous communication with client and family",
  "Updates on client's condition and daily activities",
  "Emotional support and companionship",
];

const TRUST_BADGES = [
  { icon: BadgeCheck, label: "Qualified & Trained Caregivers" },
  { icon: ShieldCheck, label: "Background Checked" },
  { icon: HandHeart, label: "Elderly & Special Needs Care" },
  { icon: Home, label: "Home or Hospital Care" },
  { icon: Clock, label: "Hourly · Daily · Monthly Plans" },
];

const FAQS = [
  {
    q: "How much does a caregiver cost in Penang?",
    a: "Our stay-out home care packages start from RM3,000 per month for 4 hours a day, 20 days per month, with a local Malaysian caregiver. Stay-in (live-in) caregiver packages start from RM5,000 per month, and a certified Malaysian nurse is RM8,500 per month. Hospital sitter duty is RM300 per 24 hours. Confirm current rates and availability with us on WhatsApp.",
  },
  {
    q: "What is the difference between a stay-out and a stay-in caregiver package?",
    a: "A stay-out caregiver comes to your home for a fixed number of hours per day (4 to 12 hours) and does not live with you. A stay-in caregiver lives in the home and provides full-time care across the month. Stay-out suits families who need daytime support; stay-in suits seniors who need round-the-clock presence.",
  },
  {
    q: "Do you provide hospital sitters and transport to appointments in Penang?",
    a: "Yes. Our hospital sitter service covers all hospitals in Penang at RM300 per 24 hours, and our appointment driver service provides safe transport to hospital or clinic visits — RM350 per appointment with our company car, or RM200 using the client's car. The caregiver-driver waits until the appointment finishes and can arrange a monthly schedule.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
        { "@type": "ListItem", position: 2, name: "Caregiver Packages in Penang", item: `${SITE.url}/caregiver-packages/` },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

const SectionHead = ({ eyebrow, title, copy }) => (
  <Reveal className="text-center max-w-2xl mx-auto">
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-teal">{eyebrow}</p>
    <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-brand-charcoal">{title}</h2>
    {copy && <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">{copy}</p>}
  </Reveal>
);

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-brand-charcoal focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/20 transition";

const PriceCard = ({ pkg, testid, onEnquire }) => (
  <div
    data-testid={testid}
    className="group flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)] hover:-translate-y-1.5 hover:shadow-[0_16px_50px_rgb(7,91,89,0.14)] transition-all duration-300"
  >
    {pkg.hours && (
      <span className="inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold text-white" style={{ backgroundColor: pkg.color }}>
        <Clock className="h-3.5 w-3.5" /> {pkg.hours}
      </span>
    )}
    <h3 className="mt-3 font-display text-lg font-semibold text-brand-charcoal">{pkg.name}</h3>
    {pkg.caregiver && <p className="mt-1 text-sm text-slate-500">{pkg.caregiver}</p>}
    {pkg.note && <p className="mt-1 text-sm text-slate-500">{pkg.note}</p>}
    <p className="mt-1 text-xs text-slate-400">{pkg.days}</p>
    <p className="mt-4 font-display text-2xl font-bold" style={{ color: pkg.color }}>
      {pkg.price}
      <span className="text-sm font-semibold text-slate-400"> {pkg.per}</span>
    </p>
    <a
      href="#package-enquiry"
      onClick={() => onEnquire(pkg.id)}
      data-testid={`enquire-${pkg.id}`}
      className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-semibold transition-colors duration-300 hover:text-white"
      style={{ borderColor: pkg.color, color: pkg.color }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = pkg.color)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
    >
      <MessageCircle className="h-4 w-4" /> Enquire
    </a>
  </div>
);

const CaregiverPackagesPage = () => {
  const [form, setForm] = useState({ packageId: "", name: "", phone: "", date: "", message: "" });
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const selectedPkg = ALL_PACKAGES.find((p) => p.id === form.packageId) || null;

  const onEnquireCard = (id) => setForm({ ...form, packageId: id });

  const onSubmit = (e) => {
    e.preventDefault();
    window.open(waLink(buildCaregiverMessage({ pkg: selectedPkg, ...form })), "_blank", "noopener");
  };

  return (
    <main data-testid="page-caregiver-packages">
      <Seo
        title="Caregiver Packages in Penang | Home Care, Stay-In Caregivers & Hospital Sitters"
        description="Caregiver packages in Penang: stay-out home care from RM3,000/month, stay-in caregivers, certified nurses, hospital sitters from RM300/24 hours and hospital appointment driver services. Qualified, background-checked Malaysian caregivers."
        path="/caregiver-packages/"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-brand-teal text-white grain">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-brand-gold/20 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-brand-coral/20 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <Breadcrumbs items={[{ label: "Caregiver Packages" }]} />
          <div className="mt-8 grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-gold">Trusted Caregivers in Penang</p>
              <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
                Caregiver Packages in Penang
              </h1>
              <p className="mt-2 font-accent italic text-2xl text-brand-gold">Quality Care. Happier Lives.</p>
              <p className="mt-4 text-base sm:text-lg text-white/80 leading-relaxed">
                Flexible home care packages with professional, background-checked Malaysian caregivers — from stay-out
                hourly care at home to full-time stay-in caregivers, certified nurses, hospital sitters and appointment
                driver services across Penang.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {PILLARS.map((p) => (
                  <span key={p.label} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90">
                    <p.icon className="h-3.5 w-3.5 text-brand-gold" /> {p.label}
                  </span>
                ))}
              </div>
              <a
                href="#package-enquiry"
                data-testid="caregiver-hero-enquire"
                className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-brand-gold px-7 py-3.5 text-sm sm:text-base font-semibold text-white shadow-[0_8px_30px_rgb(201,154,82,0.35)] hover:-translate-y-0.5 hover:bg-white hover:text-brand-teal transition-all duration-300"
              >
                <ClipboardList className="h-5 w-5" /> Enquire About a Package
              </a>
            </Reveal>
            <Reveal delay={0.15} className="relative mx-auto w-full max-w-sm">
              <div className="absolute -inset-3 rounded-[2rem] border-2 border-brand-gold/40 rotate-2" aria-hidden="true" />
              <a href="/assets/caregiver-packages-flyer.jpg" target="_blank" rel="noopener noreferrer" data-testid="flyer-link" title="View the full caregiver packages flyer">
                <img
                  src="/assets/caregiver-packages-flyer.jpg"
                  alt="Nursing Home in Penang caregiver packages flyer — home care, stay-in care, hospital care and appointment driver prices"
                  width="426"
                  height="640"
                  loading="eager"
                  className="relative rounded-[2rem] w-full h-auto shadow-2xl hover:scale-[1.02] transition-transform duration-500"
                />
              </a>
              <p className="relative mt-3 text-center text-xs text-white/60">Tap to view the full service flyer</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <SectionHead
          eyebrow="Home Care Packages"
          title="Stay Out & Flexi Home Care"
          copy="Stay-out caregivers visit your home for fixed daily hours — flexible home care for elderly family members, with professional and certified caregivers."
        />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {HOME_CARE.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.05}>
              <PriceCard pkg={p} testid={`home-care-card-${i + 1}`} onEnquire={onEnquireCard} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Stay In Care Packages"
            title="Full-Time Stay-In Caregivers & Nurses"
            copy="Live-in, full-time care at home with certified caregivers and Malaysian degree or diploma-qualified nurses."
          />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STAY_IN.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <PriceCard pkg={p} testid={`stay-in-card-${i + 1}`} onEnquire={onEnquireCard} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <SectionHead
          eyebrow="Hospital Care Services"
          title="Professional Support During Hospital Stays"
          copy="Hospital sitters covering all hospitals in Penang, plus safe appointment transport for hospital and clinic visits."
        />
        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          {HOSPITAL_CARE.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.05}>
              <PriceCard pkg={p} testid={`hospital-care-card-${i + 1}`} onEnquire={onEnquireCard} />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-10">
          <div className="rounded-3xl border border-slate-100 bg-brand-tealLight p-8 sm:p-10">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-teal text-white">
                <Car className="h-5 w-5" />
              </span>
              <h3 className="font-display text-xl font-semibold text-brand-charcoal">Appointment Driver Service</h3>
            </div>
            <p className="mt-2 text-sm text-slate-600">Safe and reliable transport to hospital / clinic appointments.</p>
            <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
              {DRIVER_POINTS.map((d) => (
                <li key={d} className="flex gap-2.5 text-sm text-slate-600">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" /> {d}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      <section id="package-enquiry" className="scroll-mt-24 bg-white py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Package Enquiry"
            title="Enquire About a Caregiver Package"
            copy="Choose a package and share your details — the form opens WhatsApp with everything pre-filled. Nothing is sent until you press send in WhatsApp."
          />
          <Reveal delay={0.1} className="mt-10">
            <form data-testid="package-enquiry-form" onSubmit={onSubmit} className="rounded-3xl border border-slate-100 bg-brand-cream p-6 sm:p-10 shadow-[0_20px_60px_rgb(7,91,89,0.08)] space-y-5">
              <div>
                <label htmlFor="package-select" className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-brand-teal">
                  Select Package
                </label>
                <select id="package-select" data-testid="package-select" required value={form.packageId} onChange={set("packageId")} className={inputCls}>
                  <option value="">Choose a caregiver package…</option>
                  {PACKAGE_GROUPS.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.items.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.hours ? ` · ${p.hours}` : ""} · {p.price}{p.per} ({p.days})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {selectedPkg && (
                  <p data-testid="package-summary" className="mt-2 rounded-xl bg-brand-tealLight px-4 py-2.5 text-sm text-brand-teal">
                    {selectedPkg.name}
                    {selectedPkg.hours ? ` (${selectedPkg.hours})` : ""} — {selectedPkg.caregiver || selectedPkg.note},{" "}
                    {selectedPkg.days}, {selectedPkg.price}
                    {selectedPkg.per}
                  </p>
                )}
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="package-name" className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-brand-teal">
                    Contact Name
                  </label>
                  <input id="package-name" data-testid="package-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                </div>
                <div>
                  <label htmlFor="package-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-brand-teal">
                    Phone / WhatsApp Number
                  </label>
                  <input id="package-phone" data-testid="package-phone-input" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="e.g. 012-345 6789" />
                </div>
              </div>
              <div>
                <label htmlFor="package-date" className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-brand-teal">
                  Preferred Start Date <span className="font-medium normal-case tracking-normal text-slate-400">(optional)</span>
                </label>
                <input id="package-date" data-testid="package-date-input" type="date" value={form.date} onChange={set("date")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="package-message" className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-brand-teal">
                  Message <span className="font-medium normal-case tracking-normal text-slate-400">(optional)</span>
                </label>
                <textarea id="package-message" data-testid="package-message-input" rows="3" value={form.message} onChange={set("message")} className={inputCls} placeholder="Tell us about the care needed — mobility, medical conditions, preferred caregiver gender…" />
              </div>
              <button
                type="submit"
                data-testid="package-submit-btn"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-teal px-7 py-4 text-base font-semibold text-white shadow-[0_8px_30px_rgb(7,91,89,0.25)] hover:bg-brand-gold hover:-translate-y-0.5 transition-all duration-300"
              >
                <MessageCircle className="h-5 w-5" /> Continue on WhatsApp
              </button>
              <p className="flex gap-2 text-xs text-slate-400 leading-relaxed">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-sage" />
                Your details are used only to respond to your enquiry. Please do not send sensitive medical records
                through this form or WhatsApp.
              </p>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <SectionHead
          eyebrow="Services Included"
          title="Comprehensive Care for Better Wellbeing"
          copy="Every caregiver package includes hands-on daily support, health monitoring and genuine companionship."
        />
        <Reveal delay={0.1} className="mt-10">
          <ul data-testid="services-included" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES_INCLUDED.map((s) => (
              <li key={s} className="flex gap-2.5 rounded-xl border border-slate-100 bg-brand-cream px-4 py-3.5 text-sm text-slate-700 shadow-[0_4px_16px_rgb(0,0,0,0.03)]">
                <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-sage" /> {s}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.15} className="mt-10 flex flex-wrap justify-center gap-3">
          {TRUST_BADGES.map((b) => (
            <span key={b.label} className="inline-flex items-center gap-2 rounded-full bg-brand-tealLight px-4 py-2 text-sm font-semibold text-brand-teal">
              <b.icon className="h-4 w-4 text-brand-gold" /> {b.label}
            </span>
          ))}
        </Reveal>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <SectionHead eyebrow="Caregiver FAQs" title="Caregiver Package Questions" />
        <Reveal delay={0.1} className="mt-10">
          <FaqAccordion faqs={FAQS} testid="caregiver-faq" />
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 text-center text-xs text-slate-400">
            Prices shown are from our current service flyer and may change. Confirm current rates and caregiver
            availability with us on WhatsApp before booking.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 md:pb-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-brand-teal grain px-6 py-14 sm:px-12 text-center text-white">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-gold/25 blur-3xl" aria-hidden="true" />
            <h2 className="relative mx-auto max-w-2xl font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Your Trusted Elderly Care Partner in Penang
            </h2>
            <p className="relative mx-auto mt-4 max-w-xl text-base sm:text-lg text-white/80">
              Tell us the care hours, caregiver type and start date you need — we will match you with a qualified,
              background-checked caregiver.
            </p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-4">
              <WhatsAppCTA testid="caregiver-final-whatsapp" message={WA_MESSAGES.homepage} label="Contact Us on WhatsApp" variant="gold" />
              <span className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-semibold text-white/90">
                <Phone className="h-4 w-4 text-brand-gold" /> {SITE.whatsappDisplay}
              </span>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default CaregiverPackagesPage;
